<script setup>
import AppBadge from '@/components/atoms/AppBadge'
import AppCard from '@/components/molecules/AppCard'
import HelpHubStepMarker from './HelpHubStepMarker.vue'
import HelpHubTip from './HelpHubTip.vue'
import { safeCapture } from '@/helpers/posthog'
import { POSTHOG_EVENTS, LINK_TARGET } from '@/constants'

const props = defineProps({
  number: { type: Number, required: true },
  title: { type: String, required: true },
  // A label beside the title.
  badge: { type: String, default: '' },
  description: { type: String, default: '' },
  // Numbered sub-steps under the title.
  list: { type: Array, default: () => [] },
  // The steps of the page this one opens, a bullet each: `{ title, summary }`.
  outline: { type: Array, default: () => [] },
  tip: { type: String, default: '' },
  // The tip's heading, and the help page the tip links to.
  tipTitle: { type: String, default: '' },
  tipTo: { type: [String, Object], default: null },
  // A help page; without a link the step is plain text.
  to: { type: [String, Object], default: null },
  // A page in the app, in place of a help page.
  route: { type: [String, Object], default: null },
  // The page this step sits on, so the event says where the link was followed from.
  slug: { type: String, default: '' },
})

// A plain step links nowhere and reports nothing.
const trackLink = () => {
  if (!props.to && !props.route) return
  safeCapture(POSTHOG_EVENTS.HELP_LINK_CLICKED, {
    slug: props.slug || null,
    target_type: props.to ? LINK_TARGET.HELP_PAGE : LINK_TARGET.CDP_ROUTE,
    target: String(props.to ?? props.route),
  })
}
</script>

<template>
  <AppCard as="li" variant="naked" size="sm" :to="to ?? route" :title="title" @click="trackLink">
    <template #leading>
      <HelpHubStepMarker size="md" variant="filled">{{ number }}</HelpHubStepMarker>
    </template>

    <template #title>
      <span :class="[to || route ? 'text-tint-1' : 'font-medium', { underline: route }]" v-text="title" />

      <!-- The locked step card's fill; mr-auto keeps the badge beside the title; Figma draws its icon at 16px. -->
      <AppBadge v-if="badge" size="xs" variant="tint-light" class="mr-auto">
        <Icon icon="heroicons-outline:sparkles" class="size-4 shrink-0" />
        {{ badge }}
      </AppBadge>

      <Icon v-if="to" icon="heroicons-solid:chevron-right" class="size-4 shrink-0 text-tint-1" />
      <!-- Figma draws this 16px arrow with a 1.5px stroke. -->
      <Icon v-else-if="route" icon="heroicons:arrow-up-right" class="size-4 shrink-0 text-tint-1 [&>path]:stroke-[2.25px]" />
    </template>

    <template #description>
      <p v-if="description" v-text="description" />

      <!-- Markers sit outside, so a wrapped line hangs under the text. -->
      <ol v-if="list.length" class="list-decimal pl-4">
        <li v-for="line in list" :key="line" v-text="line" />
      </ol>

      <ul v-if="outline.length" class="list-disc pl-4">
        <li v-for="line in outline" :key="line.title"><strong v-text="line.title" /> — {{ line.summary }}</li>
      </ul>

      <HelpHubTip v-if="tip" :title="tipTitle" :to="tipTo" compact class="mt-3">{{ tip }}</HelpHubTip>
    </template>
  </AppCard>
</template>
